// Mode-aware browser tab title.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMe } from '@/lib/auth/useMe';
import type { WorkspaceMode } from '@/lib/persona';

export const MARKETING_DOCUMENT_TITLE = 'Zoe Diagnostics — Startup diligence from source truth';

const PUBLIC_TITLE_ROUTES: RegExp[] = [
  /^\/share\//,
  /^\/invite\//,
  /^\/survey\//,
  /^\/founder-followup\//,
  /^\/legal(\/|$)/,
  /^\/signin(\/|$)/,
  /^\/login(\/|$)/,
  /^\/magic-link(\/|$)/,
  /^\/sign-up(\/|$)/,
  /^\/forgot-password(\/|$)/,
  /^\/reset-password(\/|$)/,
  /^\/start(\/|$)/,
  /^\/scan\//,
  /^\/paywall(\/|$)/,
  /^\/demo(\/|$)/,
];

export function isPublicTitleRoute(pathname: string): boolean {
  if (pathname === '/') return true;
  return PUBLIC_TITLE_ROUTES.some((re) => re.test(pathname));
}

/** Honest app tab title per persona. */
export function appTitleForMode(mode: WorkspaceMode): string {
  return mode === 'operator'
    ? 'Zoe Diagnostics — Company check-ups'
    : 'Zoe Diagnostics — Diligence desk';
}

export function useModeAwareDocumentTitle(): void {
  const location = useLocation();
  const meState = useMe({ enabled: !isPublicTitleRoute(location.pathname) });
  const mode = meState.me?.workspace?.mode;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (meState.state === 'ok' && mode) {
      document.title = appTitleForMode(mode);
    } else if (meState.state === 'unauth') {
      document.title = MARKETING_DOCUMENT_TITLE;
    }
    // 'loading'/'offline' leave the current title as-is so nothing flickers.
  }, [meState.state, mode, location.pathname]);
}
